import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SimpleMDE from 'react-simplemde-editor';
import ReactMarkdown, { Components } from 'react-markdown';
import { toast } from 'react-toastify';
import remarkGfm from 'remark-gfm';
import {
  SparkFormDefinition,
  SparkFormElement,
  SparkFormValidationIssue,
  SparkImage,
  SparkIteration,
} from '../../types/sparkTypes';
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore';
import styles from '../../styles/FormEditor.module.css';
import 'easymde/dist/easymde.min.css';
import { EditorCapabilities } from '../../types/sparkTypes';
import { normalizeMarkdownBullets } from '../../utils/markdown';
import ImageSlot from './ImageSlot';

type FormValues = Record<string, any>;

interface FormEditorProps {
  iteration: SparkIteration;
  formId: string;
  formDefinition: SparkFormDefinition | null | undefined;
  validationIssues?: SparkFormValidationIssue[] | null;
  isReadOnly?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onSubmitSuccess?: (updatedIteration: SparkIteration) => void;
  markdownComponents?: Components;
  markdownPlugins?: any[];
  projectFqn: string;
  capabilities?: Partial<EditorCapabilities>;
  editorMode?: 'iteration' | 'item';
  persistValues?: (values: Record<string, any>) => Promise<SparkIteration>;
}


interface MarkdownFieldProps {
  value: string;
  isReadOnly: boolean;
  placeholder?: string | null;
  onChange: (value: string) => void;
  markdownComponents?: Components;
  markdownPlugins?: any[];
  isEditing: boolean;
  onEnterEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving: boolean;
  hasChanges: boolean;
}

const MarkdownField: React.FC<MarkdownFieldProps> = ({
  value,
  isReadOnly,
  placeholder,
  onChange,
  markdownComponents,
  markdownPlugins,
  isEditing,
  onEnterEdit,
  onCancel,
  onSave,
  isSaving,
  hasChanges,
}) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const requestedViewportHeightRef = useRef<number | null>(null);
  const mdeRef = useRef<any>(null);
  const cmRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastViewportRef = useRef<number>(0);
  const onCancelRef = useRef(onCancel);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);
  const plugins = useMemo(() => markdownPlugins ?? [remarkGfm], [markdownPlugins]);
  const simpleMdeOptions = useMemo(
    () => ({
      spellChecker: false,
      status: false,
      placeholder: placeholder ?? '',
      autofocus: true,
      hideIcons: ['preview', 'side-by-side', 'fullscreen'] as const,
      renderingConfig: {
        singleLineBreaks: false,
      },
    }),
    [placeholder]
  );

  // Keep SimpleMDE fully controlled with current value

  const hasValue = value?.trim().length > 0;

  const previewContent = hasValue ? (
    <ReactMarkdown components={markdownComponents} remarkPlugins={plugins}>
      {normalizeMarkdownBullets(value)}
    </ReactMarkdown>
  ) : (
    <span className={styles.markdownViewerEmpty}>No content yet.</span>
  );

  const handlePreviewKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const el = previewRef.current;
        let content = 96;
        if (el) {
          const rect = el.getBoundingClientRect();
          const cs = window.getComputedStyle(el);
          const pt = parseFloat(cs.paddingTop || '0');
          const pb = parseFloat(cs.paddingBottom || '0');
          const bt = parseFloat(cs.borderTopWidth || '0');
          const bb = parseFloat(cs.borderBottomWidth || '0');
          content = Math.max(96, rect.height - pt - pb - bt - bb);
        }
        requestedViewportHeightRef.current = content;
        onEnterEdit();
      }
    },
    [onEnterEdit]
  );

  const handleEnterEditClick = useCallback(() => {
    const el = previewRef.current;
    let content = 96;
    if (el) {
      const rect = el.getBoundingClientRect();
      const cs = window.getComputedStyle(el);
      const pt = parseFloat(cs.paddingTop || '0');
      const pb = parseFloat(cs.paddingBottom || '0');
      const bt = parseFloat(cs.borderTopWidth || '0');
      const bb = parseFloat(cs.borderBottomWidth || '0');
      content = Math.max(96, rect.height - pt - pb - bt - bb);
    }
    requestedViewportHeightRef.current = content;
    onEnterEdit();
  }, [onEnterEdit]);

  useEffect(() => {
    if (!isEditing) {
      // Ensure any editor-specific sizing is cleared so preview uses natural content size
      requestedViewportHeightRef.current = null;
      return;
    }
    // When the editor mounts, set CodeMirror viewport height
    const cm: any = cmRef.current;
    if (cm) {
      try {
        // Defensive: kill CodeMirror's default 300px min-height on scroller
        const scroller: HTMLDivElement | undefined = cm.getScrollerElement?.();
        if (scroller) {
          scroller.style.minHeight = '0px';
          scroller.style.maxHeight = 'none';
        }
        // Use preview-based size at open to avoid feedback with content height
        const previewBased = Math.max(96, requestedViewportHeightRef.current ?? 96);

        // Set CodeMirror height directly
        cm.setSize('100%', previewBased);
        cm.refresh();
        // Focus editor without forcing caret position
        try {
          cm.focus?.();
          cm.scrollTo?.(0, 0);
        } catch (_) {}
        lastViewportRef.current = previewBased;
        // After refresh, ensure minHeight override persists
        if (scroller) {
          scroller.style.minHeight = '0px';
        }
      } catch (e) {
        // ignore if not ready yet
      }
    }
    const handleWindowKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancelRef.current?.();
      }
    };
    window.addEventListener('keydown', handleWindowKey);
    return () => {
      window.removeEventListener('keydown', handleWindowKey);
    };
  }, [isEditing]);

  // Watch for CodeMirror resize (when user drags resize handle) and update internal state
  useEffect(() => {
    if (!isEditing || !cmRef.current) {
      return;
    }

    const cm = cmRef.current;
    const cmElement = cm.getWrapperElement?.();

    if (!cmElement) {
      return;
    }

    // Create ResizeObserver to watch CodeMirror element size changes
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === cmElement) {
          try {
            const newHeight = entry.contentRect.height;

            // Only update if height actually changed to avoid unnecessary refreshes
            if (Math.abs(newHeight - lastViewportRef.current) > 1) {
              // Update CodeMirror's internal size to match its DOM size
              cm.setSize('100%', newHeight);
              cm.refresh();
              lastViewportRef.current = newHeight;

              // Ensure scroller constraints persist
              const scroller: HTMLDivElement | undefined = cm.getScrollerElement?.();
              if (scroller) {
                scroller.style.minHeight = '0px';
                scroller.style.maxHeight = 'none';
              }
            }
          } catch (e) {
            // Ignore if CodeMirror is not ready
          }
        }
      }
    });

    observer.observe(cmElement);
    resizeObserverRef.current = observer;

    return () => {
      observer.disconnect();
      resizeObserverRef.current = null;
    };
  }, [isEditing]);

  // Guard against losing focus when unsaved changes exist; flash UI and refocus
  const [warnUnsaved, setWarnUnsaved] = useState(false);
  useEffect(() => {
    if (!isEditing) return;
    const bounce = () => {
      setWarnUnsaved(true);
      window.setTimeout(() => setWarnUnsaved(false), 600);
      try { cmRef.current?.focus(); } catch (_) {}
    };
    const blockPointer = (e: Event) => {
      if (!isEditing || !hasChanges) return;
      const container = containerRef.current;
      if (container && !container.contains(e.target as Node)) {
        e.preventDefault();
        (e as any).stopImmediatePropagation?.();
        e.stopPropagation();
        bounce();
      }
    };
    const blockFocus = (e: FocusEvent) => {
      if (!isEditing || !hasChanges) return;
      const container = containerRef.current;
      if (container && !container.contains(e.target as Node)) {
        // Focus events are not cancelable; immediately refocus editor
        bounce();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isEditing || !hasChanges) return;
      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        bounce();
      }
    };
    // Capture as early as possible across input types
    document.addEventListener('pointerdown', blockPointer, true);
    document.addEventListener('mousedown', blockPointer, true);
    document.addEventListener('click', blockPointer, true);
    document.addEventListener('touchstart', blockPointer, { capture: true, passive: false });
    document.addEventListener('focusin', blockFocus, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', blockPointer, true);
      document.removeEventListener('mousedown', blockPointer, true);
      document.removeEventListener('click', blockPointer, true);
      document.removeEventListener('touchstart', blockPointer as any, true as any);
      document.removeEventListener('focusin', blockFocus, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [isEditing, hasChanges]);


  if (isReadOnly) {
    return (
      <div ref={previewRef} className={styles.markdownViewer}>
        {previewContent}
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div
        ref={previewRef}
        className={`${styles.markdownViewer} ${styles.markdownViewerEditable}`}
        role="button"
        tabIndex={0}
        onClick={handleEnterEditClick}
        onKeyDown={handlePreviewKeyDown}
        style={{ height: 'auto' }}
      >
        {previewContent}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.markdownEditorContainer} ${warnUnsaved ? styles.editorWarnFlash : ''}`}
    >
      <SimpleMDE
        value={value}
        onChange={onChange}
        options={simpleMdeOptions}
        getMdeInstance={(inst: any) => {
          mdeRef.current = inst;
          cmRef.current = inst?.codemirror ?? null;
          // Focus editor as soon as instance is ready so typing works immediately
          try {
            requestAnimationFrame(() => {
              cmRef.current?.focus?.();
              cmRef.current?.refresh?.();
            });
          } catch (_) {}
        }}
      />
      <div className={`${styles.markdownEditorActions} ${warnUnsaved ? styles.actionsWarnFlash : ''}`}>
        <button
          type="button"
          className={styles.markdownCancelButton}
          onClick={() => { onCancel(); requestedViewportHeightRef.current = null; }}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.markdownSaveButton}
          onClick={() => { onSave(); requestedViewportHeightRef.current = null; }}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const FormEditor: React.FC<FormEditorProps> = ({
  iteration,
  formId,
  formDefinition,
  validationIssues,
  isReadOnly = false,
  onDirtyChange,
  onSubmitSuccess,
  markdownComponents,
  markdownPlugins,
  projectFqn,
  capabilities,
  editorMode = 'iteration',
  persistValues,
}) => {
  const updateSparkIteration = useAtlasStore(
    (state: AtlasState) => state.updateSparkIteration
  );

  const [values, setValues] = useState<FormValues>(() => {
    const elementIds = (formDefinition?.groups || []).flatMap(g => g.elements.map(e => e.id));
    const flatValues = (iteration as any).values || {};
    return Object.fromEntries(elementIds.map(id => [id, flatValues[id]]).filter(([,v]) => v !== undefined));
  });
  const [isDirty, setIsDirty] = useState(false);
  const [remoteValuesSnapshot, setRemoteValuesSnapshot] = useState<string>(() => JSON.stringify(values));
  const [pendingRemoteValues, setPendingRemoteValues] = useState<FormValues | null>(null);
  const [showRemoteBanner, setShowRemoteBanner] = useState(false);
  const [editingElementId, setEditingElementId] = useState<string | null>(null);
  const [imagesState, setImagesState] = useState<Record<string, SparkImage>>(((iteration as any).images) ?? {});
  const [markdownDrafts, setMarkdownDrafts] = useState<Record<string, string>>({});
  const [savingFields, setSavingFields] = useState<Record<string, boolean>>({});
  const savedValuesRef = useRef<FormValues>(values);
  const setSparkUserInteractionBlockingRefresh = useAtlasStore(
    (state: AtlasState) => state.setSparkUserInteractionBlockingRefresh
  );
  const iterationForMedia = useMemo(() => ({ ...iteration, images: imagesState }), [iteration, imagesState]);
  const previousIterationRef = useRef<string>(iteration.fqn);
  const previousFormIdRef = useRef<string>(formId);

  const issuesByElement = useMemo(() => {
    if (!validationIssues) {
      return {};
    }
    return validationIssues.reduce<Record<string, SparkFormValidationIssue[]>>((acc, issue) => {
      const key = issue.element_id ?? '__global__';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(issue);
      return acc;
    }, {});
  }, [validationIssues]);

  const globalIssues = issuesByElement['__global__'] ?? [];

  const setDirtyState = useCallback(
    (next: boolean) => {
      if (isDirty !== next) {
        setIsDirty(next);
        onDirtyChange?.(next);
      }
    },
    [isDirty, onDirtyChange]
  );

  const markMarkdownDirty = useCallback(() => setDirtyState(true), [setDirtyState]);
  const clearMarkdownDirty = useCallback(() => setDirtyState(false), [setDirtyState]);

  const updateBlockingState = useCallback(
    (pending: Record<string, boolean>, editingId: string | null) => {
      const shouldBlock = editingId !== null || Object.values(pending).some(Boolean);
      setSparkUserInteractionBlockingRefresh(shouldBlock);
    },
    [setSparkUserInteractionBlockingRefresh]
  );

  const hasFieldChanged = useCallback((elementId: string, nextValue: any) => {
    const saved = savedValuesRef.current ? savedValuesRef.current[elementId] : undefined;
    return JSON.stringify(saved ?? null) !== JSON.stringify(nextValue ?? null);
  }, []);

  const persistFieldValue = useCallback(
    async (elementId: string, nextValue: any) => {
      if (isReadOnly) {
        return;
      }
      if (!hasFieldChanged(elementId, nextValue)) {
        return;
      }

      setSavingFields(prev => ({ ...prev, [elementId]: true }));
      try {
        const merged = {
          ...savedValuesRef.current,
          ...values,
          [elementId]: nextValue,
        };

        const updated = persistValues
          ? await persistValues(merged)
          : await updateSparkIteration(iteration.fqn, { values: merged });

        const nextFormValues = merged;
        savedValuesRef.current = nextFormValues;
        setValues(nextFormValues);
        setRemoteValuesSnapshot(JSON.stringify(nextFormValues));
        setPendingRemoteValues(null);
        setShowRemoteBanner(false);
        if (editingElementId === elementId) {
          clearMarkdownDirty();
        }
        onSubmitSuccess?.(updated);
      } catch (error) {
        console.error('Failed to save field value', error);
        toast.error('Failed to save field. Please try again.');
        setValues(savedValuesRef.current);
        throw error;
      } finally {
        setSavingFields(prev => {
          const next = { ...prev };
          delete next[elementId];
          return next;
        });
      }
    },
    [clearMarkdownDirty, editingElementId, hasFieldChanged, isReadOnly, iteration.fqn, onSubmitSuccess, updateSparkIteration, values, persistValues]
  );

  const handleFieldBlur = useCallback(
    (elementId: string, nextValue: any) => {
      persistFieldValue(elementId, nextValue).catch(() => null);
    },
    [persistFieldValue]
  );

  useEffect(() => {
    updateBlockingState(savingFields, editingElementId);
  }, [savingFields, editingElementId, updateBlockingState]);

  useEffect(() => {
    const elementIds = (formDefinition?.groups || []).flatMap(g => g.elements.map(e => e.id));
    const flatValues = (iteration as any).values || {};
    const formValues = Object.fromEntries(elementIds.map(id => [id, flatValues[id]]).filter(([,v]) => v !== undefined));
    const serialized = JSON.stringify(formValues);
    const iterationChanged = previousIterationRef.current !== iteration.fqn;
    const formChanged = previousFormIdRef.current !== formId;

    if (iterationChanged || formChanged) {
      previousIterationRef.current = iteration.fqn;
      if (formChanged) {
        previousFormIdRef.current = formId;
      }
      setEditingElementId(null);
      setMarkdownDrafts({});
      clearMarkdownDirty();
    }

    if (!isDirty || iterationChanged || formChanged) {
      setValues(formValues);
      savedValuesRef.current = formValues;
      setRemoteValuesSnapshot(serialized);
      setPendingRemoteValues(null);
      setShowRemoteBanner(false);
      return;
    }

    if (serialized !== remoteValuesSnapshot) {
      setPendingRemoteValues(formValues);
      setRemoteValuesSnapshot(serialized);
      setShowRemoteBanner(true);
    }
  }, [iteration, formId, isDirty, remoteValuesSnapshot, clearMarkdownDirty, formDefinition]);

  useEffect(() => {
    const elementIds = (formDefinition?.groups || []).flatMap(g => g.elements.map(e => e.id));
    const flatImages = (iteration as any).images || {};
    const images: Record<string, SparkImage> = {} as any;
    elementIds.forEach(id => { if (flatImages[id]) images[id] = flatImages[id]; });
    setImagesState(images);
  }, [formDefinition, iteration]);

  const handleResetToRemote = useCallback(() => {
    if (pendingRemoteValues) {
      setValues(pendingRemoteValues);
      savedValuesRef.current = pendingRemoteValues;
    } else {
      const elementIds = (formDefinition?.groups || []).flatMap(g => g.elements.map(e => e.id));
      const flatValues = (iteration as any).values || {};
      const formValues = Object.fromEntries(elementIds.map(id => [id, flatValues[id]]).filter(([,v]) => v !== undefined));
      setValues(formValues);
      savedValuesRef.current = formValues;
    }
    setPendingRemoteValues(null);
    setShowRemoteBanner(false);
    setMarkdownDrafts({});
    setEditingElementId(null);
    clearMarkdownDirty();
  }, [clearMarkdownDirty, formDefinition, iteration, pendingRemoteValues]);

  const handleKeepEditing = useCallback(() => {
    setPendingRemoteValues(null);
    setShowRemoteBanner(false);
  }, []);

  const handleValueChange = useCallback(
    (elementId: string, nextValue: any, options?: { markDirty?: boolean }) => {
      setValues(prev => ({ ...prev, [elementId]: nextValue }));
      if (options?.markDirty) {
        markMarkdownDirty();
      }
    },
    [markMarkdownDirty]
  );

  const handleClearValue = useCallback(
    (elementId: string, options?: { markDirty?: boolean }) => {
      setValues(prev => {
        const next = { ...prev };
        delete next[elementId];
        return next;
      });
      if (options?.markDirty) {
        markMarkdownDirty();
      }
    },
    [markMarkdownDirty]
  );

  const renderTextField = (element: SparkFormElement) => {
    const value = (values[element.id] ?? '') as string;
    return (
      <input
        className={styles.textInput}
        type="text"
        value={value}
        disabled={isReadOnly}
        onChange={event => handleValueChange(element.id, event.target.value)}
        onBlur={event => {
          if (isReadOnly) return;
          const nextValue = event.target.value === '' ? null : event.target.value;
          handleFieldBlur(element.id, nextValue);
        }}
        placeholder={element.content_template ?? ''}
      />
    );
  };

  const renderMarkdownField = (element: SparkFormElement) => {
    const savedValue = (values[element.id] ?? '') as string;
    const draftValue = markdownDrafts[element.id] ?? savedValue;
    const isEditing = !isReadOnly && editingElementId === element.id;
    const value = isEditing ? draftValue : savedValue;
    const isSavingField = Boolean(savingFields[element.id]);
    const hasChanges = draftValue !== savedValue;
    return (
      <MarkdownField
        value={value}
        isReadOnly={isReadOnly}
        placeholder={element.content_template}
        onChange={next => {
          if (isReadOnly) {
            return;
          }
          setMarkdownDrafts(prev => ({ ...prev, [element.id]: next }));
          markMarkdownDirty();
        }}
        markdownComponents={markdownComponents}
        markdownPlugins={markdownPlugins}
        isEditing={isEditing}
        onEnterEdit={() => {
          if (isReadOnly) {
            return;
          }
          setEditingElementId(element.id);
          setMarkdownDrafts(prev => ({ ...prev, [element.id]: savedValue }));
          // Do not mark dirty on enter; only mark when content changes
        }}
        onCancel={() => {
          setMarkdownDrafts(prev => {
            const nextDrafts = { ...prev };
            delete nextDrafts[element.id];
            return nextDrafts;
          });
          setEditingElementId(current => (current === element.id ? null : current));
          clearMarkdownDirty();
        }}
        onSave={async () => {
          const nextValue = markdownDrafts[element.id] ?? savedValue;
          try {
            await persistFieldValue(element.id, nextValue);
            setMarkdownDrafts(prev => {
              const nextDrafts = { ...prev };
              delete nextDrafts[element.id];
              return nextDrafts;
            });
            setEditingElementId(null);
            clearMarkdownDirty();
          } catch (error) {
            // leave draft for retry
          }
        }}
        isSaving={isSavingField}
        hasChanges={hasChanges}
      />
    );
  };

  const renderNumberField = (element: SparkFormElement) => {
    const value = values[element.id];
    return (
      <input
        className={styles.textInput}
        type="number"
        value={value ?? ''}
        disabled={isReadOnly}
        onChange={event => {
          const raw = event.target.value;
          if (raw === '') {
            handleClearValue(element.id);
            return;
          }
          const numeric = Number(raw);
          handleValueChange(element.id, Number.isNaN(numeric) ? raw : numeric);
        }}
        onBlur={event => {
          if (isReadOnly) return;
          const raw = event.target.value;
          if (raw === '') {
            handleFieldBlur(element.id, null);
            return;
          }
          const numeric = Number(raw);
          handleFieldBlur(
            element.id,
            Number.isNaN(numeric) ? raw : numeric
          );
        }}
        min={element.min}
        max={element.max}
        step={
          element.fractional_digits !== undefined
            ? Math.pow(10, -element.fractional_digits)
            : undefined
        }
      />
    );
  };

  const renderOptionField = (element: SparkFormElement) => {
    const options = element.options ?? [];
    if (element.multi_select) {
      const selected = new Set<string>((values[element.id] as string[]) ?? []);
      return (
        <div className={styles.optionList}>
          {options.map(option => {
            const checked = selected.has(option.id);
            return (
              <label key={option.id} className={styles.optionItem}>
                <input
                  type="checkbox"
                  disabled={isReadOnly}
                  checked={checked}
                  onChange={event => {
                    if (isReadOnly) return;
                    const next = new Set(selected);
                    if (event.target.checked) {
                      next.add(option.id);
                    } else {
                      next.delete(option.id);
                    }
                    const nextArray = Array.from(next);
                    handleValueChange(element.id, nextArray);
                    persistFieldValue(element.id, nextArray).catch(() => null);
                  }}
                />
                {option.label}
              </label>
            );
          })}
        </div>
      );
    }

    // For single-select, show placeholder when no value is set
    const current = (values[element.id] ?? null) as string | null;
    const hasValue = current !== null && current !== '';
    return (
      <select
        className={styles.selectInput}
        value={current ?? ''}
        disabled={isReadOnly}
        onChange={event => {
          if (isReadOnly) return;
          const nextValue = event.target.value || null;
          handleValueChange(element.id, nextValue);
          persistFieldValue(element.id, nextValue).catch(() => null);
        }}
      >
        {!hasValue && (
          <option value="" disabled>
            Select an option...
          </option>
        )}
        {options.map(option => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  const renderImageField = (element: SparkFormElement) => {
    const slotState = imagesState[element.id];
    const cardinality = element.cardinality ?? Math.max(1, slotState?.current_file_ids?.length ?? 0);
    const fileIds = slotState?.current_file_ids ?? [];
    const slots = Array.from({ length: cardinality }, (_, idx) => fileIds[idx] ?? null);

    const handleIterationUpdated = (updatedIteration: SparkIteration) => {
      const nextImages = (updatedIteration as any).images ?? {};
      setImagesState(nextImages);
      onSubmitSuccess?.(updatedIteration);
    };

    return (
      <div className={styles.inlineMediaField}>
        <div className={styles.inlineMediaStrip}>
          {slots.map((fileId, idx) => (
            <ImageSlot
              key={`${element.id}-${idx}`}
              elementId={element.id}
              elementLabel={element.label}
              fileId={fileId}
              index={idx}
              cardinality={cardinality}
              isReadOnly={isReadOnly}
              iteration={iterationForMedia}
              projectFqn={projectFqn}
              capabilities={{
                canEditMedia: capabilities?.canEditMedia ?? !isReadOnly,
                canUseMediaAI: capabilities?.canUseMediaAI ?? true,
              }}
              editorMode={editorMode ?? 'iteration'}
              onIterationUpdated={handleIterationUpdated}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderField = (element: SparkFormElement) => {
    switch (element.type) {
      case 'text':
        return renderTextField(element);
      case 'textarea':
        return renderMarkdownField(element);
      case 'number':
        return renderNumberField(element);
      case 'option':
        return renderOptionField(element);
      case 'image':
        return renderImageField(element);
      default:
        return null;
    }
  };

  if (!formDefinition) {
    return (
      <div className={styles.formEditor}>
        <p>No profile form is configured for this creative type.</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.formEditor}>
        {showRemoteBanner && (
          <div className={styles.remoteBanner}>
            <span>
              This iteration was updated elsewhere. Reload the latest values or continue editing your local changes.
            </span>
          <div className={styles.remoteBannerActions}>
            <button className={styles.secondaryButton} onClick={handleKeepEditing}>
              Keep editing
            </button>
            <button className={styles.primaryButton} onClick={handleResetToRemote}>
              Reload
            </button>
          </div>
        </div>
      )}

      {globalIssues.length > 0 && (
        <div className={styles.globalIssues}>
          <ul className={styles.validationList}>
            {globalIssues.map(issue => (
              <li
                key={`${issue.code}-${issue.message}`}
                className={issue.level === 'error' ? styles.validationError : styles.validationWarning}
              >
                {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.groups}>
        {formDefinition.groups.map(group => {
          const showHeader = (formDefinition.groups?.length ?? 0) > 1;
          return (
            <section key={group.label} className={styles.group}>
              {showHeader && (
                <header className={styles.groupHeader}>
                  <h3>{group.label}</h3>
                  {group.description && <p className={styles.fieldDescription}>{group.description}</p>}
                </header>
              )}
              <div className={styles.groupBody}>
                {group.elements.map(element => {
                  const elementIssues = issuesByElement[element.id] ?? [];
                  const isImageElement = element.type === 'image';
                  const currentValue = (values as any)[element.id];
                  const imageSlot = imagesState[element.id];
                  const emptyImage = isImageElement && (!imageSlot || !(imageSlot.current_file_ids || []).length);
                  const emptyValue =
                    currentValue === null ||
                    currentValue === undefined ||
                    (typeof currentValue === 'string' && currentValue.trim().length === 0) ||
                    (Array.isArray(currentValue) && currentValue.length === 0);
                  const hasMissingRequired = Boolean(element.required) && (emptyValue || emptyImage);
                  return (
                    <div key={element.id} className={styles.field}>
                      <div className={styles.fieldHeader}>
                        <div className={styles.fieldHeading}>
                          <span className={styles.fieldLabel}>
                            {element.label}
                            {element.required && <span className={styles.requiredMarker}>*</span>}
                          </span>
                        </div>
                        {element.description && (
                          <p className={styles.fieldDescription}>{element.description}</p>
                        )}
                      </div>
                      <div className={`${styles.fieldBlock} ${hasMissingRequired ? styles.fieldError : ''}`}>{renderField(element)}</div>
                      {elementIssues.length > 0 && (
                        <ul className={styles.validationList}>
                          {elementIssues.map(issue => (
                            <li
                              key={`${element.id}-${issue.code}-${issue.message}`}
                              className={
                                issue.level === 'error'
                                  ? styles.validationError
                                  : styles.validationWarning
                              }
                            >
                              {issue.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

    </div>

    </>
  );
};

export default FormEditor;
